import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

// The card-advance motion for both judging decks — a card leaves in the
// direction of travel while the next one arrives from the opposite edge, so
// moving through a deck reads as movement instead of a silent content swap.
//
// ── WHY IT IS NOT ACTUALLY A TINDER CARD ────────────────────────────────────
//
// The reference is a swipe deck, but two of the things that make one feel like
// one are banned outright by DESIGN.md §7.2:
//
//   ❌ Rotation effects on UI elements   — the card tilt as it flies off
//   ❌ Spring physics, bounce, elastic   — the throw-and-settle
//
// (The unused GlowupDeck.js still carries a `rotate` transform, which is
// plausibly why it was never wired to a page.)
//
// So this keeps the READ of a deck — directional travel, one card yielding to
// the next — using only sanctioned motion: a lateral translate and a fade on
// the system's own mechanical curve, well inside the 1.2s ceiling. If the
// house style ever wants the tilt, it is one `rotate` away, but that is a
// DESIGN.md amendment rather than a component change.
//
//   forward  (direction  1):  outgoing ←──  incoming ──← from the right
//   backward (direction -1):  outgoing ──→  incoming →── from the left
//
// `mode="popLayout"`, not "wait". Sequencing the two means the next card does
// not exist until the previous one has finished leaving, which on a deck
// people move through quickly reads as a stall — and it made the surface
// genuinely asynchronous, so even the tests could no longer find the next card
// after a SKIP. popLayout lifts the outgoing card out of flow so the incoming
// one takes its place immediately and the two overlap, which is both faster
// and closer to the deck being imitated. The wrapper below supplies the
// positioning context that lifting needs.

const OFFSET_PX = 56;
// The same curve as ease-mogged / the Censor Bar slide (DESIGN.md §7.1).
const EASE = [0.65, 0, 0.35, 1];

export default function DeckTransition({ cardKey, direction = 1, children, className = '' }) {
  // Honours prefers-reduced-motion: the card still changes and still fades,
  // it simply does not travel. Dropping the animation entirely would take the
  // feedback away from the people most likely to need the change announced.
  const reduce = useReducedMotion();
  const travel = reduce ? 0 : OFFSET_PX * direction;

  return (
    <div className={`relative ${className}`.trim()}>
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.div
          key={cardKey}
          initial={{ opacity: 0, x: travel }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -travel }}
          transition={{
            duration: reduce ? 0.12 : 0.24,
            ease: EASE,
          }}
        >
          {children}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
